import { useEffect } from "react";
import { useQuery } from "@apollo/react-hooks";
import queryString from "query-string";
import gql from "graphql-tag";
import get from "lodash/get";

import goToUrl from "shared/util/goToUrl";
import isApp from "shared/util/isApp";

// Whitelist of redirect query param locations.
export const REDIRECT_PARAM_WHITELIST = [
	"/upgrade",
	"/daterschoice",
	"/settings?section=connections",
];

/**
 * @private
 * Sometimes we'll have a `redirect` query param. Handle that.
 * @param {string} query - The current query string.
 * @returns {boolean} Whether there was a redirect or not.
 */
export const _handleRedirectParam = (query = "") => {
	const { redirect } = queryString.parse(query);
	const shouldRedirect = redirect && REDIRECT_PARAM_WHITELIST.includes(redirect);

	if (shouldRedirect) {
		goToUrl(redirect);
	}

	return shouldRedirect;
};

export const MANDATORY_REDIRECT_GATEKEEPERS = {
	GDPR_AGE_REDIRECT: "GDPR_AGE_REDIRECT",
	ONBOARDING_MANDATORY_REDIRECT: "ONBOARDING_MANDATORY_REDIRECT",
	REBOARDING_MANDATORY_REDIRECT: "REBOARDING_MANDATORY_REDIRECT",
	NON_MONOGAMY_REBOARDING: "NON_MONOGAMY_REBOARDING",
	TERMS_MANDATORY_REDIRECT: "TERMS_MANDATORY_REDIRECT",
	REALNAMES_REDIRECT: "REALNAMES_REDIRECT",
	SMS_MANDATORY_REDIRECT: "SMS_MANDATORY_REDIRECT",
};

export const MANDATORY_REDIRECTS = {
	[MANDATORY_REDIRECT_GATEKEEPERS.GDPR_AGE_REDIRECT]: "/underage",
	[MANDATORY_REDIRECT_GATEKEEPERS.ONBOARDING_MANDATORY_REDIRECT]: "/onboarding",
	[MANDATORY_REDIRECT_GATEKEEPERS.REBOARDING_MANDATORY_REDIRECT]: "/onboarding",
	[MANDATORY_REDIRECT_GATEKEEPERS.NON_MONOGAMY_REBOARDING]: "/onboarding",
	[MANDATORY_REDIRECT_GATEKEEPERS.TERMS_MANDATORY_REDIRECT]: "/terms-update",
	[MANDATORY_REDIRECT_GATEKEEPERS.REALNAMES_REDIRECT]: "/realname",
	[MANDATORY_REDIRECT_GATEKEEPERS.SMS_MANDATORY_REDIRECT]: "/phone",
};

export const MANDATORY_REDIRECT_QUERY_PARAMS = {
	[MANDATORY_REDIRECT_GATEKEEPERS.REBOARDING_MANDATORY_REDIRECT]: {
		"needs_reboarding": 1,
	},
	[MANDATORY_REDIRECT_GATEKEEPERS.NON_MONOGAMY_REBOARDING]: {
		"needs_reboarding": 1,
		"non_monogamy_reboarding": 1,
	},
};

/**
 * @private
 * Handle any mandatory redirects
 * @param {string} path - The current path.
 * @param {string} redirectGatekeepers - Array of relevant gatekeepers.
 * @returns {boolean} Whether there was a redirect or not.
 */
export const _handleMandatoryRedirects = (path = "", redirectGatekeepers = {}) => {
	// Check if any of our gatekeepers are enabled,
	// and if so, redirect the user to the path.
	const redirect = Object.keys(MANDATORY_REDIRECT_GATEKEEPERS)
		.find((redirectKey) => redirectGatekeepers[redirectKey]);
	const shouldRedirect = !!(redirect && path !== MANDATORY_REDIRECTS[redirect]);
	const queryParams = {
		cf: path,
		...(MANDATORY_REDIRECT_QUERY_PARAMS[redirect] || {}),
	};

	if (shouldRedirect) {
		const redirectUrl = `${MANDATORY_REDIRECTS[redirect]}?${queryString.stringify(queryParams)}`;
		goToUrl(redirectUrl);
	}

	return shouldRedirect;
};

const GET_REDIRECT_GATEKEEPERS = gql`
	query getRedirectGatekeepers {
		session {
			gatekeeperChecks {
				GDPR_AGE_REDIRECT
				ONBOARDING_MANDATORY_REDIRECT
				REBOARDING_MANDATORY_REDIRECT
				TERMS_MANDATORY_REDIRECT
				REALNAMES_REDIRECT
				NON_MONOGAMY_REBOARDING
				SMS_MANDATORY_REDIRECT
			}
		}
	}
`;

function useSiteRedirect({ pathname, search }) {
	const { data } = useQuery(GET_REDIRECT_GATEKEEPERS, {
		skip: isApp(),
	});
	const redirectGatekeepers = get(data, "session.gatekeeperChecks");

	useEffect(() => {
		if (!redirectGatekeepers) {
			return;
		}

		// Handle any mandatory redirects (i.e. underage).
		if (_handleMandatoryRedirects(pathname, redirectGatekeepers)) {
			return;
		}

		// Handle having a "redirect" query param.
		_handleRedirectParam(search);
	}, [redirectGatekeepers, pathname, search]);
}

export default useSiteRedirect;



// WEBPACK FOOTER //
// ./src/routers/shared/hooks/useSiteRedirect.js